import api from './api';
import { ApiResponse } from '../types';
import { FeeCategory, FeeCategoryInput } from '../types/fees';

/**
 * Thin service wrapper for /api/fee-categories. Mirrors the shape of
 * revenueCategoryService so any component that already reads revenue
 * categories can be templated for fee categories.
 */

export const getFeeCategories = async (params?: {
  search?: string;
  includeInactive?: boolean;
  schoolId?: string;
}): Promise<FeeCategory[]> => {
  const sp = new URLSearchParams();
  if (params?.search) sp.append('search', params.search);
  if (params?.includeInactive) sp.append('includeInactive', 'true');
  if (params?.schoolId) sp.append('schoolId', params.schoolId);
  const response = await api.get<ApiResponse<{ categories: FeeCategory[] }>>(`/fee-categories?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!.categories;
};

export const createFeeCategory = async (data: FeeCategoryInput): Promise<FeeCategory> => {
  const response = await api.post<ApiResponse<FeeCategory>>('/fee-categories', data);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const updateFeeCategory = async (id: string, data: Partial<FeeCategoryInput>): Promise<FeeCategory> => {
  const response = await api.put<ApiResponse<FeeCategory>>(`/fee-categories/${id}`, data);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const deleteFeeCategory = async (id: string): Promise<void> => {
  const response = await api.delete<ApiResponse>(`/fee-categories/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
};

export const reorderFeeCategories = async (order: { id: string; displayOrder: number }[]): Promise<void> => {
  const response = await api.post<ApiResponse>('/fee-categories/reorder', { order });
  if (!response.data.success) throw new Error(response.data.message);
};

const feeCategoryService = {
  getFeeCategories,
  createFeeCategory,
  updateFeeCategory,
  deleteFeeCategory,
  reorderFeeCategories,
};

export default feeCategoryService;
